import { motion } from 'framer-motion';
import {
  Sparkles,
  BarChart3,
  Megaphone,
  Users,
  Palette,
  LineChart,
} from 'lucide-react';

const services = [
  {
    icon: Sparkles,
    title: 'AI Content Creation',
    description:
      'On-brand posts, captions, and visuals generated and scheduled by AI in minutes — not hours.',
  },
  {
    icon: BarChart3,
    title: 'Smart Analytics',
    description:
      'Real-time dashboards that surface what works, what doesn’t, and exactly what to do next.',
  },
  {
    icon: Megaphone,
    title: 'Paid Ads Automation',
    description:
      'AI-tuned campaigns across Meta, Google, and TikTok — optimized for ROAS, not impressions.',
  },
  {
    icon: Users,
    title: 'Audience Intelligence',
    description:
      'Find lookalikes, segment by intent, and personalize messaging at scale across every channel.',
  },
  {
    icon: Palette,
    title: 'Brand Strategy',
    description:
      'A unified voice, look, and narrative — built once, distributed everywhere your audience lives.',
  },
  {
    icon: LineChart,
    title: 'Performance Reports',
    description:
      'Weekly executive-ready reports auto-generated from your data, with AI-written insights.',
  },
];

export default function Services() {
  return (
    <section
      id="services"
      className="relative bg-background text-foreground py-24 md:py-32 px-8 md:px-28"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mb-16 md:mb-20"
        >
          <div className="liquid-glass inline-flex items-center gap-2 px-3 py-1.5 rounded-lg mb-6">
            <span className="text-xs font-medium text-muted-foreground tracking-wide uppercase">
              What we do
            </span>
          </div>
          <h2 className="text-4xl md:text-6xl font-medium tracking-[-1.5px] leading-[1.05]">
            Everything you need to{' '}
            <span className="font-serif italic font-normal">grow.</span>
          </h2>
          <p className="mt-5 text-base md:text-lg text-muted-foreground max-w-xl">
            Six AI-powered capabilities, one connected platform. Replace the
            scattered tools and weekly fire drills with a single intelligent
            workflow.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {services.map((service, i) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                whileHover={{ y: -4 }}
                className="group relative rounded-2xl border border-border bg-card p-7 hover:bg-white/[0.04] hover:border-white/15 transition-colors transform-gpu"
              >
                <div className="liquid-glass inline-flex items-center justify-center h-11 w-11 rounded-xl mb-5">
                  <Icon className="h-5 w-5 text-foreground" />
                </div>
                <h3 className="text-xl font-semibold tracking-tight mb-2">
                  {service.title}
                </h3>
                <p className="text-sm leading-6 text-muted-foreground">
                  {service.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
